import { useState, useEffect } from 'react'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from '../ui'
import { toast } from 'sonner'

export interface WhitelistedAddress {
  id: string
  address: string
  chain: string
  label?: string | null
  status?: string | null
  decisionBy?: string | null
}

interface ManageWhitelistedAddressModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  address: WhitelistedAddress | null
  onUpdateAddress: (variables: { input: { id: string; label: string } }) => Promise<{ data?: any }>
  onDeleteAddress: (variables: { input: { id: string } }) => Promise<{ data?: any }>
}

export function ManageWhitelistedAddressModal({
  open,
  onOpenChange,
  address,
  onUpdateAddress,
  onDeleteAddress,
}: ManageWhitelistedAddressModalProps) {
  const [label, setLabel] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Update local label state when address changes
  useEffect(() => {
    if (address) {
      setLabel(address.label || '')
    }
  }, [address])

  const handleSave = async () => {
    if (!address) return

    setIsSaving(true)
    try {
      const result = await onUpdateAddress({
        input: {
          id: address.id,
          label: label.trim(),
        },
      })

      if (result.data) {
        toast.success('Address label updated successfully!')
        onOpenChange(false)
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    } catch (error) {
      console.error('Error updating address:', error)
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!address) return

    setIsSubmitting(true)
    try {
      const result = await onDeleteAddress({ input: { id: address.id } })
      if (result.data) {
        toast.success('Whitelisted address has been deleted successfully!')
        onOpenChange(false)
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    } catch (error) {
      console.error('Error deleting address:', error)
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!address) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <DialogTitle className="text-xl font-semibold">Whitelisted Address</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <div className="flex gap-2">
              <Input
                id="title"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Enter address title"
                className="flex-1"
              />
              <div className="flex items-center justify-center">
                <Button variant="main_action" onClick={handleSave} className="px-6 rounded-md">
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {address.status && (
              <div>
                <span className="text-sm font-medium text-gray-600">Status: </span>
                <span className="font-semibold">
                  {address.status.charAt(0).toUpperCase() + address.status.slice(1).toLowerCase()}
                </span>
              </div>
            )}
            <div>
              <span className="text-sm font-medium text-gray-600">Address: </span>
              <span className="text-sm break-all">{address.address}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Chain: </span>
              <span className="font-semibold">{address.chain}</span>
            </div>
            {address.decisionBy && (
              <div>
                <span className="text-sm font-medium text-gray-600">Decision by: </span>
                <span className="text-sm text-gray-600">{address.decisionBy}</span>
              </div>
            )}
          </div>

          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
            >
              {isSubmitting ? 'Deleting...' : 'Delete Address'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
